import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة
import { 
  Save, Search, Gauge, List, Filter, Download, Maximize2, 
  History, Info, Activity, ChevronRight, Settings, Trash2, 
  MapPin, Paperclip, ArrowLeft, ChevronLeft, Type, Minus, Plus
} from 'lucide-react';
import UniversalLookup from './UniversalLookup';
import AttachmentsModal from './AttachmentsModal';
import { useFontSize } from './FontSizeContext';
import { usePermissions } from './usePermissions'; // افترضت وجود الصلاحيات

// --- 🎯 تمرير isRTL و t للـ Component المساعد ---
const WhereUsedTable = ({ title, icon, data, columns, onRowClick, isRTL, t }) => (
  <div className="bg-white border rounded shadow-sm overflow-hidden">
    <div className={`p-2 bg-slate-50 border-b font-black text-slate-500 uppercase text-[9px] tracking-widest flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
      <div className={`flex items-center gap-2 italic ${isRTL ? 'flex-row-reverse' : ''}`}>
        {icon} {title}
      </div>
      <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-[8px]">{data?.length || 0} {t('common.records', 'Records')}</span>
    </div>
    <div className="overflow-x-auto">
      <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
        <thead className="bg-gray-50 text-gray-400 text-[9px] uppercase font-black border-b">
          <tr>
            {columns.map(col => <th key={col.key} className="p-2 border-x border-gray-100">{col.label}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 text-[10px]">
          {data?.length > 0 ? data.map((row, idx) => (
            <tr key={idx} onClick={() => onRowClick(row)} className="hover:bg-blue-50 cursor-pointer transition-colors group">
              {columns.map(col => (
                <td key={col.key} className="p-2 border-x border-gray-50 font-bold text-blue-900 group-hover:text-blue-600">
                  {row[col.key] || '---'}
                </td>
              ))}
            </tr>
          )) : (
            <tr>
              <td colSpan={columns.length} className="p-4 text-center text-gray-300 italic uppercase tracking-tighter">
                {t('meters.where_used.no_assoc', 'No associations found in this category.')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const MeterMaster = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canCreate, canUpdate, canDelete } = usePermissions('meters');
  
  const [activeTab, setActiveTab] = useState('Meter');
  const [fieldMaps, setFieldMaps] = useState({});
  const [whereUsed, setWhereUsed] = useState({ assets: [], locations: [], items: [] });
  const [loadingWhereUsed, setLoadingWhereUsed] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [attachCount, setAttachCount] = useState(0);
  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
  const [stats, setStats] = useState({ last_reading: 0, last_reading_date: 'N/A', total_rollovers: 0 });

  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const [meter, setMeter] = useState({
    meter_num: '', meter_name: '', description: '', meter_type: 'CONTINUOUS',
    unit_of_measure: '', active: true, remarks: ''
  });

  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', domainId: '', field: '' });

  // 1. محرك النافيجيشن
  const loadNavigation = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await axios.get(`/api/meters/${id}/navigation${location.search}`);
      setNav(res.data);
    } catch (err) { console.error("Nav load failed"); }
  }, [id, isNew, location.search]);

  // 2. جلب المرفقات
  const fetchAttachCount = useCallback(async () => {
    if (isNew || !meter.meter_num) return;
    try {
      const res = await axios.get(`/api/attachments/count/METERS/${meter.meter_num}`);
      setAttachCount(res.data.count || 0);
    } catch (e) { console.error(e); }
  }, [isNew, meter.meter_num]);

  // 3. جلب بيانات الاستخدام
  const fetchWhereUsed = async () => {
    setLoadingWhereUsed(true);
    try {
      const res = await axios.get(`/api/meters/${id}/where-used`);
      setWhereUsed(res.data);
    } catch (err) { console.error("Error fetching where used data:", err); }
    finally { setLoadingWhereUsed(false); }
  };
  
  const fetchMeterStats = useCallback(async () => {
    if (isNew) return;
    try {
        const res = await axios.get(`/api/meters/${id}/statistics`);
        setStats(res.data);
    } catch (err) { console.error("Stats load failed"); }
  }, [id, isNew]);

  useEffect(() => {
      if (!isNew) fetchMeterStats();
  }, [fetchMeterStats, isNew]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const maps = await axios.get('/api/admin/field-map/Meters');
        setFieldMaps(maps.data || {});

        if (!isNew) {
          const res = await axios.get(`/api/meters/${id}`);
          setMeter(res.data);
          loadNavigation();
          fetchAttachCount();
        }
      } catch (err) { console.error("Error loading meter data:", err); }
    };
    loadData();
  }, [id, isNew, loadNavigation, fetchAttachCount]);

  useEffect(() => {
    if (activeTab === 'Where Used' && !isNew) {
      fetchWhereUsed();
    }
  }, [activeTab, isNew]);

  const handleSmartSearch = (fieldName, title, defaultType) => {
    const linkedDomain = fieldMaps[fieldName];
    setLookup({ 
      isOpen: true, title, 
      type: linkedDomain ? 'DOMAIN' : defaultType, 
      domainId: linkedDomain || '', 
      field: fieldName 
    });
  };

  const handleSave = async () => {
    const loadId = toast.loading(t('common.saving', "Saving..."));
    try {
      await axios.post('/api/meters/save', meter);
      toast.success(t('common.save_success', "Saved Successfully!"), { id: loadId });
      // 🎯 الحفاظ على الكويري بعد الحفظ (لو كان عنصر جديد)
      if (isNew) navigate(`/meters/${meter.meter_num}${location.search}`);
    } catch (err) { 
      toast.error(t('common.save_fail', "Save Failed: ") + (err.response?.data?.detail || err.message), { id: loadId }); 
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: t('common.are_you_sure', 'Are you sure?'),
      text: t('meters.details.delete_warn', { meter_num: meter.meter_num }),
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#3085d6',
      confirmButtonText: t('common.yes_delete', 'Yes, delete it!'),
      cancelButtonText: t('common.cancel', 'Cancel'),
      customClass: { popup: `${isRTL ? 'font-cairo' : 'font-sans'} text-[12px]` }
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/api/meters/${meter.meter_num}`, { headers: { 'Authorization': `Bearer ${token}` } });
        toast.success(t('common.delete_success', "Deleted successfully"));
        navigate(`/meters${location.search}`); // 🎯 الرجوع بالكويري
      } catch (err) {
        toast.error(t('common.delete_fail', "Delete failed: ") + (err.response?.data?.detail || "Unknown error"));
      }
    }
  };

  const goTo = (targetId) => targetId && navigate(`/meters/${targetId}${location.search}`);

  return (
    <div className={`flex flex-col h-screen bg-[#f3f4f6] uppercase tracking-tighter overflow-hidden transition-[font-size] duration-300 ${isRTL ? 'font-cairo' : 'font-sans'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      <UniversalLookup {...lookup} onClose={() => setLookup({...lookup, isOpen: false})} 
        onSelect={(row) => {
          const val = row.value || row.id || row.unit_of_measure;
          setMeter(prev => ({ ...prev, [lookup.field]: val }));
          setLookup(prev => ({ ...prev, isOpen: false }));
        }} 
      />

      <AttachmentsModal 
        isOpen={isDocModalOpen} 
        onClose={() => {setIsDocModalOpen(false); fetchAttachCount();}} 
        ownertable="METERS" 
        ownerid={meter.meter_num} 
      />

      {/* --- 1. TOP TOOLBAR (Control Center) --- */}
      <div className={`bg-[#1a3353] text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4`}>
        <div className={`flex items-center gap-3 ${isRTL ? 'mr-2' : 'ml-2'}`}>
          {/* 🎯 زرار الرجوع السحري اللي بيحفظ الفلتر */}
          <button onClick={() => navigate(`/meters${location.search}`)} className="p-1.5 hover:bg-white/10 rounded-full text-blue-200 transition-all">
            {isRTL ? <ChevronRight size={18} /> : <ArrowLeft size={18} />}
          </button>
          
          {!isNew && (
            <div className="flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1">
              <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1">
                <span className="text-orange-400 font-black">{nav.current_pos}</span> / {nav.total}
              </div>
              <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
          )}
        </div>

        <div className={`flex items-center gap-2 ${isRTL ? 'ml-2' : 'mr-2'}`}>
          {(isNew ? canCreate : canUpdate) && (
            <button onClick={handleSave} className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-sm flex items-center gap-2 text-[11px] font-black shadow-md transition-all uppercase">
              <Save size={14} /> {t('common.save', 'SAVE')}
            </button>
          )}

          <div className="w-[1px] h-6 bg-white/10 mx-1" />

          <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 hover:bg-white/10 rounded text-blue-200">
            <Paperclip size={16} />
            {attachCount > 0 && <span className="absolute top-1 right-1 bg-red-600 text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black animate-pulse">{attachCount}</span>}
          </button>

          {!isNew && canDelete && (
            <button onClick={handleDelete} className="p-2 hover:bg-red-500/20 rounded text-red-400 transition-colors" title={t('common.delete', 'Delete')}>
              <Trash2 size={16}/>
            </button>
          )}

          {/* Font Controls */}
          <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 ml-4">
            <button onClick={() => fontContext.changeFontSize(-1)} className="hover:text-orange-400"><Minus size={14} /></button>
            <div className="flex items-center gap-1 min-w-[40px] justify-center text-blue-200">
              <Type size={14} /><span className="font-black text-[10px]">{fontSize}</span>
            </div>
            <button onClick={() => fontContext.changeFontSize(1)} className="hover:text-orange-400"><Plus size={14} /></button>
          </div>
        </div>
      </div>

      {/* --- 2. HEADER BAR --- */}
      <div className={`bg-white p-3 border-b flex items-center gap-10 shadow-sm z-20`}>
        <div className={`flex flex-col min-w-[120px] ${isRTL ? 'mr-4' : 'ml-4'}`}>
          <label className="text-[9px] text-gray-400 font-black uppercase tracking-widest">{t('meters.fields.meter_num', 'METER NUMBER')}</label>
          <input 
            className={`text-sm font-black text-[#1a3353] outline-none border-b border-transparent focus:border-blue-500 bg-transparent uppercase ${!isNew && 'opacity-60'}`}
            value={meter.meter_num}
            disabled={!isNew}
            onChange={e => setMeter({...meter, meter_num: e.target.value.toUpperCase()})}
            placeholder="NEW_METER"
          />
        </div>
        <div className={`flex flex-col flex-1 border-gray-200 ${isRTL ? 'border-r pr-8' : 'border-l pl-8'}`}>
          <label className="text-[9px] text-gray-400 font-black uppercase tracking-widest">{t('assets.headers.desc', 'DESCRIPTION')}</label>
          <input 
            className="text-sm font-bold text-gray-700 outline-none focus:text-blue-600 bg-transparent w-full transition-all"
            value={meter.description}
            onChange={e => setMeter({...meter, description: e.target.value})}
            placeholder={t('assets.headers.desc', 'Enter description...') + "..."}
          />
        </div>
      </div>

      {/* --- 3. TABS BAR --- */}
      <div className="flex bg-white border-b border-gray-100 px-4 overflow-x-auto no-scrollbar shadow-inner">
        {[
          { id: 'Meter', label: t('meters.tabs.meter', 'Meter Details'), icon: <Gauge size={13}/> },
          { id: 'Where Used', label: t('meters.tabs.where_used', 'Where Used'), icon: <List size={13}/> }
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} 
            className={`px-8 py-3 flex items-center gap-2 transition-all whitespace-nowrap text-[10px] font-black uppercase tracking-tighter ${isRTL ? 'border-l border-gray-100/50' : 'border-r border-gray-100/50'}
            ${activeTab === t.id ? 'border-b-2 border-[#1a3353] text-[#1a3353] bg-blue-50/30' : 'text-gray-400 hover:text-gray-600'}`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* --- 4. CONTENT AREA --- */}
      <div className="flex-1 overflow-y-auto p-4 custom-scrollbar bg-[#f8f9fb]">
        
        {activeTab === 'Meter' && (
          <div className="grid grid-cols-12 gap-6 max-w-7xl mx-auto animate-in fade-in duration-500">
            <div className="col-span-8 space-y-6">
              <SectionCard title={t('meters.sections.info', 'Meter Information')} isRTL={isRTL}>
                <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                  <EditableField label={t('meters.fields.meter_name', 'Meter Name')} value={meter.meter_name} onChange={v => setMeter({...meter, meter_name: v})} isRequired />
                  <DataField label={t('meters.fields.type', 'Meter Type')} value={meter.meter_type} onSearch={() => handleSmartSearch('meter_type', t('meters.fields.type'), 'DOMAIN')} />
                  <DataField label={t('meters.fields.uom', 'Unit of Measure')} value={meter.unit_of_measure} onSearch={() => handleSmartSearch('unit_of_measure', t('meters.fields.uom'), 'DOMAIN')} />
                  
                  <div className={`flex items-center gap-2 pt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <input 
                      type="checkbox" 
                      checked={meter.active} 
                      onChange={(e) => setMeter({...meter, active: e.target.checked})} 
                      className="w-4 h-4 rounded accent-blue-600 cursor-pointer" 
                    />
                    <span className="font-bold text-blue-900 uppercase">{t('meters.fields.active', 'Active?')}</span>
                  </div>
                </div>
              </SectionCard>

              <SectionCard title={t('meters.sections.additional', 'Additional Details')} isRTL={isRTL}>
                <div className="flex flex-col gap-1">
                  <label className="text-gray-400 font-bold text-[9px] uppercase tracking-tighter">{t('meters.fields.remarks', 'Remarks / Notes')}</label>
                  <textarea 
                    className={`border-b border-gray-200 outline-none h-24 resize-none text-[12px] focus:border-blue-500 bg-transparent transition-all ${isRTL ? 'text-right' : ''}`}
                    value={meter.remarks || ''}
                    onChange={e => setMeter({...meter, remarks: e.target.value})}
                  />
                </div>
              </SectionCard>
            </div>

            <div className="col-span-4 space-y-6">
              <SectionCard title={t('meters.sections.stats', 'Meter Statistics')} isRTL={isRTL}>
                  <div className="text-[10px] space-y-3 text-gray-500 font-bold">
                      <p className="flex justify-between border-b pb-1">
                          {t('meters.fields.last_reading', 'LAST READING')}: <span className="text-blue-900 font-black">{stats.last_reading?.toFixed(2)}</span>
                      </p>
                      <p className="flex justify-between border-b pb-1">
                          {t('meters.fields.last_date', 'LAST READING DATE')}: <span className="text-blue-700 font-mono">{stats.last_reading_date}</span>
                      </p>
                      <p className="flex justify-between border-b pb-1">
                          {t('meters.fields.rollovers', 'TOTAL ROLLOVERS')}: <span className="text-blue-700 font-black">{stats.total_rollovers}</span>
                      </p>
                  </div>
              </SectionCard>

            </div>
          </div>
        )}

        {activeTab === 'Where Used' && (
          <div className="flex flex-col gap-6 max-w-7xl mx-auto animate-in slide-in-from-right duration-500 pb-10">
            {loadingWhereUsed ? (
              <div className="text-center py-20 italic text-gray-400 font-bold uppercase animate-pulse tracking-widest">
                {t('meters.where_used.searching', '🔍 Searching across the system...')}
              </div>
            ) : (
              <>
                <WhereUsedTable 
                  title={t('meters.where_used.linked_assets', 'Linked Assets')} 
                  icon={<Activity size={14} className="text-orange-500" />}
                  data={whereUsed.assets}
                  isRTL={isRTL} t={t}
                  columns={[
                    { label: t('meters.where_used.asset_num', 'Asset Num'), key: 'assetnum' },
                    { label: t('assets.headers.desc', 'Description'), key: 'description' },
                    { label: t('meters.where_used.site', 'Site'), key: 'site_id' },
                    { label: t('meters.where_used.status', 'Status'), key: 'status' }
                  ]}
                  onRowClick={(row) => navigate(`/assets/${row.asset_id}`)}
                />

                <WhereUsedTable 
                  title={t('meters.where_used.linked_locations', 'Linked Locations')} 
                  icon={<MapPin size={14} className="text-blue-500" />}
                  data={whereUsed.locations}
                  isRTL={isRTL} t={t}
                  columns={[
                    { label: t('meters.where_used.location', 'Location'), key: 'location_id' },
                    { label: t('assets.headers.desc', 'Description'), key: 'description' },
                    { label: t('meters.fields.type', 'Type'), key: 'type' },
                    { label: t('meters.where_used.status', 'Status'), key: 'status' }
                  ]}
                  onRowClick={(row) => navigate(`/locations/${row.location_id}`)}
                />

                <WhereUsedTable 
                  title={t('meters.where_used.linked_items', 'Linked Items')} 
                  icon={<Settings size={14} className="text-green-500" />}
                  data={whereUsed.items}
                  isRTL={isRTL} t={t}
                  columns={[
                    { label: t('meters.where_used.item_num', 'Item Num'), key: 'itemnum' },
                    { label: t('assets.headers.desc', 'Description'), key: 'description' },
                    { label: t('meters.where_used.category', 'Category'), key: 'category' }
                  ]}
                  onRowClick={(row) => navigate(`/inventory/${row.itemnum}`)}
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// --- Reusable Shared Helpers (Visual Consistency) ---

const SectionCard = ({ title, children, isRTL }) => (
  <div className="bg-white border rounded shadow-sm overflow-hidden">
    <div className={`p-2 bg-slate-50 border-b font-black text-slate-500 uppercase text-[0.9em] tracking-widest flex items-center gap-2 italic `}>
      <div className="w-1.5 h-3 bg-blue-800 rounded-full"></div> {title}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const DataField = ({ label, value, onSearch, isHighlight }) => (
  <div className="flex flex-col border-b border-gray-100 py-1.5 group hover:bg-blue-50/30 transition-all rounded px-1">
    <label className="text-[0.9em] font-bold text-gray-400 uppercase tracking-tighter">{label}</label>
    <div className="flex justify-between items-center h-5">
      <span className={`font-bold uppercase truncate ${isHighlight ? 'text-green-700 text-sm' : 'text-blue-900'}`}>{value || '---'}</span>
      {onSearch && <Search size={13} className="text-blue-400 cursor-pointer hover:text-blue-800 transition-colors hover:scale-110" onClick={onSearch} />}
    </div>
  </div>
);

const EditableField = ({ label, value, onChange, onSearch, isRequired }) => (
  <div className="flex flex-col gap-1 border-b border-gray-300 group">
    <label className={`${isRequired ? 'text-red-800' : 'text-gray-400'} font-bold text-[0.9em] uppercase tracking-tighter`}>{label}</label>
    <div className="flex items-center justify-between pb-1">
      <input className="outline-none font-bold text-blue-900 bg-transparent w-full" value={value || ''} onChange={e => onChange && onChange(e.target.value)} />
      {onSearch && <Search size={13} className="text-blue-400 cursor-pointer hover:text-blue-800 transition-colors hover:scale-110" onClick={onSearch} />}
    </div>
  </div>
);

export default MeterMaster;